import { IPickerOption, PickerPosition, PickerUnavailableType, TPickerPosition, TPickerUnavailableType } from '../types';
import { LOCALES } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { TCategory } from 'src/types/categories';
import { IProductV4 } from 'src/types/product';
import { getTranslate } from 'src/utils/helpers';
import { getDropdownHeights } from 'src/utils/styles';

export const transformToPickerOption = (option: any): IPickerOption => {
  return {
    value: option.id,
    label: typeof option.name === 'string' ? localeStore.t(option.name) : getTranslate(option.name),
    status: option.status,
    out_of_stock: option.out_of_stock,
  };
};

export const onOptionSelect = (props: {
  isMulti?: boolean;
  noReset?: boolean;
  option: IPickerOption;
  selectedOptions: IPickerOption[];
  toggleOpen: (close: boolean) => void;
  onSelect: (options: IPickerOption[]) => void;
}) => {
  const { option, selectedOptions } = props;
  const alreadySelected: boolean = selectedOptions.some((item) => item.value === option.value);
  const closePicker: boolean = props.noReset ? selectedOptions.length === 1 && alreadySelected : false;

  if (closePicker) {
    props.toggleOpen(true);
    return;
  }

  if (props.isMulti) {
    props.onSelect(alreadySelected ? selectedOptions.filter((item) => item.value !== option.value) : selectedOptions.concat(option));
  } else {
    props.onSelect(alreadySelected ? [] : [option]);
    props.toggleOpen(true);
  }
};

export const getUnavailableMessage = (props: { item?: any; unavailableType?: TPickerUnavailableType }): string | null => {
  if (!props.item || !props.unavailableType) return null;

  if (props.unavailableType === PickerUnavailableType.POS_INT) {
    return 'please_disable_posint';
  }

  if (props.unavailableType === PickerUnavailableType.CATEGORY) {
    const _item = props.item as TCategory;

    if (!_item.status) return 'not_available';

    return null;
  }

  if (props.unavailableType === PickerUnavailableType.PRODUCT_FOR_MEAL) {
    const _item = props.item as IProductV4;

    switch (true) {
      case !_item.category?.status:
        return 'category_not_available';
      case !_item.status:
        return 'not_available';
      case Boolean(_item.out_of_stock):
        return 'out_of_stock';
      case Boolean(_item.is_size):
        return 'sizes_cm_conflict';
      // TODO: combo exclusions
      case Boolean(_item.is_exclusions):
        return 'exclusion_cm_conflict';
      case Boolean(_item.is_donation):
        return 'donatable_cm_conflict';
      default:
        return null;
    }
  }

  return null;
};

export const getPickersPosition = (props: {
  position: TPickerPosition;
  wrapperRef?: HTMLDivElement | null;
  listRef?: HTMLUListElement | null;
  maxListWidth?: number;
  //
  listPlacement: 'top' | 'bottom';
  listLength: number;
  isSearchable?: boolean;
  numberVisibleOptionsInDropdown?: number;
  smallOptions?: boolean;
}): React.CSSProperties => {
  if (!props.wrapperRef?.getClientRects()[0] || !props.listRef?.getClientRects()[0]) return {};
  // current locale is 'english'
  const isEnLocale: boolean = localeStore.language === LOCALES.EN;
  // current placement is 'top'
  const isTopPlacement: boolean = props.listPlacement === 'top';
  // current picker position is 'absolute'
  const isAbsolutePosition: boolean = props.position === PickerPosition.ABSOLUTE;
  // height, maxHeight and minHeight of list
  const heights = getDropdownHeights(props.listLength, props.isSearchable, props.numberVisibleOptionsInDropdown, props.smallOptions);
  const { minHeight } = heights;
  let { height, maxHeight } = heights;
  height = Number(height) || 0;
  maxHeight = Number(maxHeight) || 0;
  // for calculations with location on top, smallest height value is needed.
  const heightForTopPlacement: number = height > maxHeight ? maxHeight : height;
  // picker wrapper params
  const { width: wrapperWidth, height: wrapperHeight, y: wrapperY, x: wrapperX } = props.wrapperRef.getClientRects()[0];
  // list params
  const { width: listWidth } = props.listRef.getClientRects()[0];
  // amount of offset from wrapper
  const topGap: number = 10;

  // styles
  let position: 'absolute' | 'fixed' | undefined;
  let top: string | number = 0;
  let left: string | number = 0;
  let right: string | number = 0;

  if (isAbsolutePosition) {
    position = 'absolute';
    top = isTopPlacement ? -(heightForTopPlacement + topGap) : wrapperHeight + topGap;
    left = isEnLocale ? 0 : wrapperWidth - listWidth;
  } else {
    position = 'fixed';
    top = isTopPlacement ? wrapperY - heightForTopPlacement - topGap : wrapperY + wrapperHeight + topGap;

    if (isEnLocale) {
      left = wrapperX;
    } else {
      right = document.body.clientWidth - wrapperX - wrapperWidth;
    }
  }

  // list height with top margins
  const listHeight: number = wrapperY + wrapperHeight + topGap + maxHeight;

  if (isTopPlacement) {
    // if list is located 'top' and crawls out of screen, then we limit its 'maxHeight' and change 'top' style
    if (wrapperY < heightForTopPlacement + topGap) {
      top = isAbsolutePosition ? -(wrapperY - topGap) : 0;
      maxHeight = wrapperY - topGap;
    }
  } else {
    // if list is located 'bottom' and crawls out of screen, then we limit its 'maxHeight'
    if (listHeight > window.innerHeight) {
      maxHeight = maxHeight - (listHeight - window.innerHeight);
    }
  }

  return { top, left, right, position, maxWidth: props.maxListWidth || wrapperWidth, zIndex: 1000, height, maxHeight, minHeight };
};
